/**
 * API Client Utility
 * Centralized HTTP client for all API requests
 * Follows KISS principle - simple, clean, reusable
 */

import {
  API_CONFIG,
  buildApiUrl,
  ApiResponse,
  RequestConfig,
  ApiError,
  getApiConfig
} from './config';
import { getDeviceFingerprint } from '../utils/deviceFingerprint';
import { createTimeoutSignal } from './timeout';


// ─── ADMIN TOKEN STORAGE KEYS (shared with AdminAuthContext) ───
const ADMIN_ACCESS_TOKEN_KEY = 'amalq_admin_access_token';
const ADMIN_REFRESH_TOKEN_KEY = 'amalq_admin_refresh_token';
const ADMIN_TOKEN_EXPIRY_KEY = 'amalq_admin_token_expiry';
const ADMIN_SESSION_KEY = 'amalq_admin_session';

/**
 * Strip developer-only details out of error messages before they reach
 * user-facing UI (modals, toasts, banners). Backends sometimes embed
 * internal markers — endpoint paths, migration notes, "use X instead"
 * breadcrumbs — that are useful in logs but never appropriate for users.
 *
 * Triggers replacement when the message contains any of:
 *  - an absolute API path (`/api/v1/...`)
 *  - migration / deprecation language ("replaced by", "retired", "deprecated")
 *  - imperative pointers to internal endpoints ("use /...", "instead")
 */
const INTERNAL_LEAK_PATTERNS = [
  /\/api\/v\d+\//i,
  /replaced by/i,
  /\bretired\b/i,
  /\bdeprecated\b/i,
  /use\s+\/[a-z]/i,
];

function sanitizeErrorMessage(raw: unknown, status: number): string {
  if (typeof raw !== 'string' || raw.length === 0) {
    return status === 410
      ? 'This action is no longer available. Please refresh and try again.'
      : 'Something went wrong. Please try again.';
  }

  const looksInternal = INTERNAL_LEAK_PATTERNS.some(pattern => pattern.test(raw));
  if (looksInternal) {
    if (status === 410) {
      return 'This action is no longer available. Please refresh and try again.';
    }
    return 'Something went wrong. Please try again.';
  }

  return raw;
}

// ─── ADMIN 401 REFRESH + RETRY ───
// Singleton promise ensures concurrent 401s share a single refresh call
let adminRefreshPromise: Promise<boolean> | null = null;

/**
 * Attempt to refresh the admin access token using the stored refresh token.
 * Uses raw fetch (not apiClient) to avoid the request interceptor re-attaching
 * the stale access token. Concurrent callers share the same inflight promise.
 */
async function attemptAdminTokenRefresh(): Promise<boolean> {
  if (adminRefreshPromise) return adminRefreshPromise;

  adminRefreshPromise = (async () => {
    const refreshToken = localStorage.getItem(ADMIN_REFRESH_TOKEN_KEY);
    if (!refreshToken) return false;

    try {
      const config = getApiConfig();
      const url = `${config.baseURL}/api/v1/admin/auth/refresh`;

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      });

      if (!response.ok) return false;

      const data = await response.json();

      if (data.success && data.data?.tokens) {
        // Store new tokens
        localStorage.setItem(
          ADMIN_ACCESS_TOKEN_KEY,
          data.data.tokens.accessToken
        );
        localStorage.setItem(
          ADMIN_REFRESH_TOKEN_KEY,
          data.data.tokens.refreshToken
        );

        // Store access token expiry timestamp (expiresIn is in seconds)
        const expiresIn = data.data.tokens.expiresIn || 900;
        localStorage.setItem(
          ADMIN_TOKEN_EXPIRY_KEY,
          String(Date.now() + expiresIn * 1000)
        );

        // Store updated session data
        if (data.data.session) {
          localStorage.setItem(
            ADMIN_SESSION_KEY,
            JSON.stringify(data.data.session)
          );
        }

        // Notify AdminAuthContext to reschedule its proactive timer
        window.dispatchEvent(
          new CustomEvent('admin-token-refreshed', {
            detail: { expiresIn }
          })
        );

        return true;
      }

      return false;
    } catch {
      return false;
    }
  })();

  try {
    return await adminRefreshPromise;
  } finally {
    adminRefreshPromise = null;
  }
}

// Request/Response interceptors for global handling
interface RequestInterceptor {
  (config: RequestConfig): RequestConfig | Promise<RequestConfig>;
}

interface ResponseInterceptor {
  (response: Response, data: Record<string, unknown>): Record<string, unknown>;
}

class ApiClient {
  private defaultTimeout: number;
  private requestInterceptors: RequestInterceptor[] = [];
  private responseInterceptors: ResponseInterceptor[] = [];

  constructor() {
    this.defaultTimeout = API_CONFIG.timeout;
  }

  // Add request interceptor
  addRequestInterceptor(interceptor: RequestInterceptor): void {
    this.requestInterceptors.push(interceptor);
  }

  // Add response interceptor
  addResponseInterceptor(interceptor: ResponseInterceptor): void {
    this.responseInterceptors.push(interceptor);
  }

  // Build full URL from endpoint
  private buildUrl(endpoint: string): string {
    const url = buildApiUrl(endpoint);
    // Building API URL
    return url;
  }

  // Apply request interceptors
  private async applyRequestInterceptors(
    config: RequestConfig
  ): Promise<RequestConfig> {
    let processedConfig = config;

    for (const interceptor of this.requestInterceptors) {
      processedConfig = await interceptor(processedConfig);
    }

    return processedConfig;
  }

  // Apply response interceptors
  private async applyResponseInterceptors(
    response: Response,
    data: any
  ): Promise<any> {
    let processedData = data;

    for (const interceptor of this.responseInterceptors) {
      processedData = await interceptor(response, processedData);
    }

    return processedData;
  }

  /**
   * Extract headers from Response object as a plain Record
   * Converts Headers object to a simple key-value map
   */
  private extractHeaders(response: Response): Record<string, string> {
    const headers: Record<string, string> = {};
    response.headers.forEach((value, key) => {
      headers[key] = value;
    });
    return headers;
  }

  // Handle HTTP errors
  private handleHttpError(response: Response): never {
    const error: ApiError = {
      code: `HTTP_${response.status}`,
      message: response.statusText || 'HTTP Error',
      statusCode: response.status
    };

    throw error;
  }

  // Main request method
  async request<T = any>(config: RequestConfig): Promise<ApiResponse<T>> {
    try {
      // Apply request interceptors
      const processedConfig = await this.applyRequestInterceptors(config);

      // Build URL
      const url = this.buildUrl(processedConfig.endpoint);

      // Check if data is FormData for proper handling
      const isFormData = processedConfig.data instanceof FormData;

      // Build request options
      const requestOptions: RequestInit = {
        method: processedConfig.method,
        headers: {
          // Don't set Content-Type for FormData - let browser set it with boundary
          ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
          ...processedConfig.headers
        },
        signal: createTimeoutSignal(
          processedConfig.timeout || this.defaultTimeout
        )
      };

      // Add body for non-GET requests
      if (processedConfig.data && processedConfig.method !== 'GET') {
        if (isFormData) {
          // Pass FormData directly - don't JSON.stringify it
          requestOptions.body = processedConfig.data as FormData;
        } else {
          // JSON stringify for regular data
          requestOptions.body = JSON.stringify(
            processedConfig.data as Record<string, unknown>
          );
        }
      }

      // Add query parameters for GET requests
      let finalUrl = url;
      if (
        processedConfig.params &&
        Object.keys(processedConfig.params).length > 0
      ) {
        const searchParams = new URLSearchParams();

        const serializeParam = (key: string, value: any): void => {
          if (value === undefined || value === null) return;

          if (Array.isArray(value)) {
            // Handle arrays by appending multiple values with same key
            value.forEach(item => {
              searchParams.append(key, String(item));
            });
          } else if (typeof value === 'object') {
            // Handle objects by JSON serializing (for complex filters)
            searchParams.append(key, JSON.stringify(value));
          } else {
            // Handle primitive values
            searchParams.append(key, String(value));
          }
        };

        Object.entries(processedConfig.params).forEach(([key, value]) => {
          serializeParam(key, value);
        });

        finalUrl += `?${searchParams.toString()}`;
      }

      // Make the request
      const response = await fetch(finalUrl, requestOptions);

      // Extract headers early (before async operations)
      const responseHeaders = this.extractHeaders(response);

      // Check if response is a blob (PDF, images, etc.)
      const contentType = response.headers.get('content-type');
      const isBlob =
        contentType?.includes('application/pdf') ||
        contentType?.includes('image/') ||
        contentType?.includes('application/octet-stream');

      // Detect empty bodies (HTTP 204 No Content, or zero-length 200) so we
      // don't try to JSON.parse '' and throw. Backend uses 204 for fire-and-forget
      // mutations like KYC submit and payout-account submit-for-review.
      const isEmptyBody =
        response.status === 204 ||
        response.headers.get('content-length') === '0';

      let data;
      if (isBlob) {
        // Handle blob responses (PDF, images, etc.)
        data = await response.blob();
      } else if (isEmptyBody) {
        // No body to parse -- callers of 204 endpoints just need success/failure.
        data = {};
      } else {
        // Parse JSON responses
        data = await response.json();
      }

      // Apply response interceptors (skip for blob responses)
      let processedData = data;
      if (!isBlob) {
        processedData = await this.applyResponseInterceptors(response, data);
      }

      // Handle HTTP 202 CAPTCHA challenge responses
      // Backend returns 202 for invisible CAPTCHA with success: false, captchaRequired: true
      if (
        response.status === 202 &&
        !isBlob &&
        processedData &&
        typeof processedData === 'object'
      ) {
        // Return as structured response - let caller handle CAPTCHA challenge
        return {
          success: false,
          data: processedData,
          message: processedData.message || 'Challenge required',
          error: processedData.error,
          timestamp: processedData.timestamp || new Date().toISOString(),
          headers: responseHeaders
        };
      }

      // Check if this is a structured error response from backend (skip for blob responses)
      if (
        !response.ok &&
        !isBlob &&
        processedData &&
        typeof processedData === 'object' &&
        'success' in processedData &&
        processedData.success === false
      ) {
        // ─── ADMIN 401: Refresh + Retry ───
        // When an admin endpoint returns 401, attempt a silent token refresh
        // and retry the original request once. Only force logout if refresh fails.
        if (response.status === 401) {
          const ep = processedConfig.endpoint ?? '';
          const isAdminEndpoint =
            ep.includes('/admin/') || ep.includes('/verification/admin/');
          const isRefreshEndpoint = ep.includes('/admin/auth/refresh');

          if (isAdminEndpoint && !isRefreshEndpoint && !config._isRetry) {
            const refreshed = await attemptAdminTokenRefresh();
            if (refreshed) {
              // Strip the stale Authorization header so the interceptor
              // attaches the fresh token from localStorage on the retry.
              // Preserve any other custom headers (Accept, etc.).
              const { headers: staleHeaders, ...retryBase } = config;
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { Authorization: _stale, ...preservedHeaders } =
                staleHeaders || {};
              return this.request<T>({
                ...retryBase,
                headers:
                  Object.keys(preservedHeaders).length > 0
                    ? preservedHeaders
                    : undefined,
                _isRetry: true
              });
            }

            // Refresh failed -- force logout via browser event
            window.dispatchEvent(
              new CustomEvent('admin-auth-expired', {
                detail: { endpoint: ep, message: processedData.message }
              })
            );
          } else if (
            isAdminEndpoint &&
            (isRefreshEndpoint || config._isRetry)
          ) {
            // Refresh endpoint itself returned 401, or retry still failed -- force logout
            window.dispatchEvent(
              new CustomEvent('admin-auth-expired', {
                detail: { endpoint: ep, message: processedData.message }
              })
            );
          }
        }

        // Return the structured error response with a sanitized user-facing
        // message. Raw `processedData.message` may contain dev breadcrumbs
        // (legacy paths, migration notes) that should never reach end users.
        const rawMessage =
          processedData.message || processedData.error || 'Request failed';
        return {
          success: false,
          statusCode: response.status,
          data: processedData,
          message: sanitizeErrorMessage(rawMessage, response.status),
          error: processedData.error,
          timestamp: processedData.timestamp || new Date().toISOString(),
          headers: responseHeaders
        };
      }

      // Handle HTTP errors for non-structured responses
      if (!response.ok) {
        this.handleHttpError(response);
      }

      // Return standardized response for successful requests with headers
      return {
        success: true,
        data: processedData,
        message: isBlob
          ? 'File downloaded successfully'
          : processedData.message || 'Request successful',
        timestamp: new Date().toISOString(),
        headers: responseHeaders
      };
    } catch (error) {
      console.error('API Request failed:', error);

      // Handle different error types
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('Request timeout - please try again');
        }

        if (
          error.message.includes('Failed to fetch') ||
          error.message.includes('NetworkError')
        ) {
          throw new Error('Network error - please check your connection');
        }

        // If it's already a structured error from the backend, re-throw it
        if (
          error.message &&
          (error.message.includes('Admin account is temporarily locked') ||
            error.message.includes('Admin credentials invalid') ||
            error.message.includes('islamicGuidance') ||
            error.message.includes('temporarily restricted'))
        ) {
          throw error;
        }

        throw error;
      }

      // For non-Error objects, provide a more specific message
      if (error && typeof error === 'object' && 'message' in error) {
        throw new Error(String(error.message));
      }

      throw new Error(
        'Network request failed - please check your connection and try again'
      );
    }
  }

  // Convenience methods for HTTP verbs
  async get<T = any>(
    endpoint: string,
    params?: Record<string, any>,
    config?: Partial<RequestConfig>
  ): Promise<ApiResponse<T>> {
    return this.request<T>({
      method: 'GET',
      endpoint,
      params,
      ...config
    });
  }

  async post<T = any>(
    endpoint: string,
    data?: any,
    config?: Partial<RequestConfig>
  ): Promise<ApiResponse<T>> {
    return this.request<T>({
      method: 'POST',
      endpoint,
      data,
      ...config
    });
  }

  async put<T = any>(
    endpoint: string,
    data?: any,
    config?: Partial<RequestConfig>
  ): Promise<ApiResponse<T>> {
    return this.request<T>({
      method: 'PUT',
      endpoint,
      data,
      ...config
    });
  }

  async delete<T = any>(
    endpoint: string,
    config?: Partial<RequestConfig>
  ): Promise<ApiResponse<T>> {
    return this.request<T>({
      method: 'DELETE',
      endpoint,
      ...config
    });
  }

  async patch<T = any>(
    endpoint: string,
    data?: any,
    config?: Partial<RequestConfig>
  ): Promise<ApiResponse<T>> {
    return this.request<T>({
      method: 'PATCH',
      endpoint,
      data,
      ...config
    });
  }
}

// Token refresh is now handled by AdminAuthContext automatically

// Create and export singleton instance
export const apiClient = new ApiClient();

// ─── ADMIN TOKEN INTERCEPTOR (must be registered FIRST) ───
// Catches ALL /admin/ and /verification/admin/ endpoints.
// Registered before user interceptors so admin paths are never
// accidentally matched by user-token rules.
apiClient.addRequestInterceptor((config: RequestConfig) => {
  const ep = config.endpoint ?? '';
  const isAdminEndpoint = ep.includes('/admin/') || ep.includes('/admin?');

  // On retries after a 401 token refresh, always override Authorization
  // with the fresh token from localStorage (defense-in-depth for stale tokens).
  // On first requests, only attach if not already present (backward compatible).
  if (isAdminEndpoint && (config._isRetry || !config.headers?.Authorization)) {
    const adminToken = localStorage.getItem('amalq_admin_access_token');
    if (adminToken) {
      return {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${adminToken}`
        }
      };
    }
  }

  return config;
});

// ─── USER TOKEN INTERCEPTORS ───

// User endpoints, profile, and uploads
apiClient.addRequestInterceptor((config: RequestConfig) => {
  if (
    (config.endpoint?.startsWith('/user/') ||
      config.endpoint?.startsWith('/users/') ||
      config.endpoint?.startsWith('/profile') ||
      config.endpoint?.startsWith('/uploads/')) &&
    !config.headers?.Authorization
  ) {
    const userToken = localStorage.getItem('amalq_user_access_token');
    if (userToken) {
      return {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${userToken}`
        }
      };
    }
  }

  return config;
});

// User auth endpoints (2FA, password, activity, email verification)
// Excludes /admin/auth/* -- those are handled by the admin interceptor above
apiClient.addRequestInterceptor((config: RequestConfig) => {
  const ep = config.endpoint ?? '';
  if (
    ep.includes('/auth/') &&
    !ep.includes('/admin/auth/') &&
    !config.headers?.Authorization
  ) {
    const userToken = localStorage.getItem('amalq_user_access_token');
    if (userToken) {
      return {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${userToken}`
        }
      };
    }
  }

  return config;
});

// User verification endpoints
// Excludes /verification/admin/* -- those are handled by the admin interceptor above
apiClient.addRequestInterceptor((config: RequestConfig) => {
  const ep = config.endpoint ?? '';
  if (
    ep.includes('/verification/') &&
    !ep.includes('/verification/admin/') &&
    !config.headers?.Authorization
  ) {
    const userToken = localStorage.getItem('amalq_user_access_token');
    if (userToken) {
      return {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${userToken}`
        }
      };
    }
  }

  return config;
});

// User notification endpoints (/notifications/*)
// Admin notification endpoints (/admin/notifications/*) are already caught
// by the admin interceptor above since they contain '/admin/'
apiClient.addRequestInterceptor((config: RequestConfig) => {
  const ep = config.endpoint ?? '';
  if (
    ep.startsWith('/notifications') &&
    !ep.includes('/admin/') &&
    !config.headers?.Authorization
  ) {
    const userToken = localStorage.getItem('amalq_user_access_token');
    if (userToken) {
      return {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${userToken}`
        }
      };
    }
  }

  return config;
});

// Chat conversations list (requires auth, but not sub-paths)
apiClient.addRequestInterceptor((config: RequestConfig) => {
  if (
    config.endpoint?.includes('/chat/conversations') &&
    !config.endpoint?.includes('/chat/conversations/') &&
    !config.headers?.Authorization
  ) {
    const userToken = localStorage.getItem('amalq_user_access_token');
    if (userToken) {
      return {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${userToken}`
        }
      };
    }
  }

  return config;
});

// ─── DEVICE FINGERPRINT INTERCEPTOR ───
// Adds fingerprint header to user auth and profile requests for device trust tracking.
// Excludes login/register/anonymous to avoid CORS issues during initial auth.
// Excludes admin auth paths -- admin sessions use separate trust mechanisms.
apiClient.addRequestInterceptor((config: RequestConfig) => {
  const ep = config.endpoint ?? '';
  if (
    (ep.includes('/auth/') &&
      !ep.includes('/admin/auth/') &&
      !ep.includes('/auth/login') &&
      !ep.includes('/auth/register') &&
      !ep.includes('/auth/anonymous')) ||
    ep.includes('/user/') ||
    ep.includes('/profile')
  ) {
    try {
      const deviceFingerprint = getDeviceFingerprint() || '';

      if (deviceFingerprint) {
        return {
          ...config,
          headers: {
            ...config.headers,
            'X-Device-Fingerprint': deviceFingerprint
          }
        };
      }
    } catch (error) {
      // If device fingerprinting fails, silently continue without fingerprint
    }
  }

  return config;
});

// Export for testing or multiple instances if needed
export { ApiClient };

// Helper functions for common patterns
export const apiHelpers = {
  // Add authorization header
  withAuth: (token: string) => ({
    headers: {
      Authorization: `Bearer ${token}`
    }
  }),

  // Add custom headers
  withHeaders: (headers: Record<string, string>) => ({
    headers
  }),

  // Add timeout
  withTimeout: (timeout: number) => ({
    timeout
  }),

  // Combine multiple options
  combine: (...options: Partial<RequestConfig>[]): Partial<RequestConfig> => {
    return options.reduce((acc, option) => ({ ...acc, ...option }), {});
  }
};
